import { Component, OnInit, OnChanges, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GoogleDriveImagesService, GoogleDriveImage } from '../../../services/google-drive-images.service';
import { ToastService } from '../../../services/toast.service';
import { I18nService } from '../../../i18n/i18n.service';

@Component({
  selector: 'app-google-drive-image-selector',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './google-drive-image-selector.component.html',
  styleUrls: ['./google-drive-image-selector.component.scss']
})
export class GoogleDriveImageSelectorComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Input() title = 'Google Drive画像を選択';
  @Output() imageSelected = new EventEmitter<{ file: File; imageData: GoogleDriveImage }>();
  @Output() closed = new EventEmitter<void>();

  images: GoogleDriveImage[] = [];
  filteredImages: GoogleDriveImage[] = [];
  isLoading = false;
  searchQuery = '';
  selectedImage: GoogleDriveImage | null = null;

  constructor(
    private googleDriveService: GoogleDriveImagesService,
    private toastService: ToastService,
    public i18n: I18nService
  ) {}

  ngOnInit(): void {
    if (this.isOpen) {
      this.loadImages();
    }
  }

  ngOnChanges(): void {
    if (this.isOpen && this.images.length === 0) {
      this.loadImages();
    }
  }

  async loadImages(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.googleDriveService.getAvailableImages().toPromise();
      if (response?.success) {
        // 各画像のURLをプロキシURL経由に変換
        this.images = response.data.map((image: GoogleDriveImage) => ({
          ...image,
          thumbnailUrl: this.googleDriveService.getProxyImageUrl(image.imageUrl)
        }));
        this.filteredImages = [...this.images];
        this.toastService.showToast(this.i18n.t('drivePicker.loadedCount', { count: response.count }), 'success');
      } else {
        this.toastService.showToast(this.i18n.t('drivePicker.loadError'), 'error');
      }
    } catch (error) {
      console.error('Failed to load images:', error);
      this.toastService.showToast(this.i18n.t('drivePicker.loadErrorUnexpected'), 'error');
    } finally {
      this.isLoading = false;
    }
  }

  onSearchChange(): void {
    if (!this.searchQuery.trim()) {
      this.filteredImages = [...this.images];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredImages = this.images.filter(image =>
      image.prompt.toLowerCase().includes(query) ||
      image.generatedDate.toLowerCase().includes(query)
    );
  }

  selectImage(image: GoogleDriveImage): void {
    this.selectedImage = image;
  }

  async confirmSelection(): Promise<void> {
    if (!this.selectedImage) {
      this.toastService.showToast(this.i18n.t('drivePicker.selectPlease'), 'warning');
      return;
    }

    try {
      this.isLoading = true;
      const filename = `google_drive_${this.selectedImage.imageId}.jpg`;
      const file = await this.googleDriveService.urlToFile(this.selectedImage.imageUrl, filename);

      this.imageSelected.emit({
        file,
        imageData: this.selectedImage
      });

      this.toastService.showToast(this.i18n.t('drivePicker.selectedSuccess'), 'success');
      this.close();
    } catch (error) {
      console.error('Failed to select image:', error);
      this.toastService.showToast(this.i18n.t('drivePicker.selectError'), 'error');
    } finally {
      this.isLoading = false;
    }
  }

  close(): void {
    this.selectedImage = null;
    this.searchQuery = '';
    this.closed.emit();
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP');
    } catch {
      return dateString;
    }
  }

  truncatePrompt(prompt: string, maxLength: number = 50): string {
    return prompt.length > maxLength ? prompt.substring(0, maxLength) + '...' : prompt;
  }

  trackByImageId(_index: number, image: GoogleDriveImage): string {
    return image.imageId;
  }
}